import { useCallback, useEffect, useState } from 'react';
import type { Fan } from '@clutch/core';
import { store } from './client';
import type { ClutchClient } from './api';

const key = (mode: 'server' | 'fixture') => (mode === 'fixture' ? 'clutch.fan.fixture' : 'clutch.fan');

export function loadFan(mode: 'server' | 'fixture'): Fan | undefined {
  const raw = store.get(key(mode));
  if (!raw) return undefined;
  try {
    const f = JSON.parse(raw) as Fan;
    return typeof f?.fanId === 'string' && typeof f.displayName === 'string' ? f : undefined;
  } catch {
    return undefined;
  }
}

export function useFan(client: ClutchClient) {
  const mode = client.mode;
  const [fan, setFanState] = useState<Fan | undefined>(() => loadFan(mode));
  const [ready, setReady] = useState(mode === 'fixture');
  const setFan = useCallback(
    (f: Fan | undefined) => {
      if (f) store.set(key(mode), JSON.stringify(f));
      else store.remove(key(mode));
      setFanState(f);
    },
    [mode],
  );
  useEffect(() => {
    let alive = true;
    client.session().then((session) => { if (alive && session.required) setFan(session.fan); })
      .catch(() => undefined).finally(() => alive && setReady(true));
    return () => { alive = false; };
  }, [client, setFan]);
  return [fan, setFan, ready] as const;
}
